'use client';
import React, { useState, useRef } from 'react';
import { Upload, CheckCircle2, X, Eye, ArrowRight, ChevronLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface UploadedFile {
  id: string;
  name: string;
  size: string;
  type: string;
  docType: string;
  status: 'uploading' | 'uploaded' | 'error';
}

const docSlots = [
  { id: 'slot-passport', label: 'Passport (bio data page)', required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { id: 'slot-bank', label: 'Bank statement (last 3 months)', required: true, accept: '.pdf' },
  { id: 'slot-photo', label: 'Passport photos (×2)', required: true, accept: '.jpg,.jpeg,.png' },
  { id: 'slot-insurance', label: 'Travel insurance certificate', required: true, accept: '.pdf' },
  { id: 'slot-flight', label: 'Flight itinerary / booking', required: true, accept: '.pdf' },
  { id: 'slot-hotel', label: 'Hotel / accommodation confirmation', required: true, accept: '.pdf' },
  { id: 'slot-employment', label: 'Employment letter / proof of income', required: false, accept: '.pdf' },
  { id: 'slot-other', label: 'Additional supporting documents', required: false, accept: '.pdf,.jpg,.jpeg,.png' },
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Step5DocumentUpload({ onNext, onBack }: Props) {
  const [uploads, setUploads] = useState<Record<string, UploadedFile>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const requiredCount = docSlots.filter((s) => s.required).length;
  const uploadedRequired = docSlots.filter((s) => s.required && uploads[s.id]?.status === 'uploaded').length;
  const canContinue = uploadedRequired >= 4; // at least 4 of 6 required docs

  const simulateUpload = (slotId: string, file: File) => {
    const newFile: UploadedFile = {
      id: `upload-${slotId}-${Date.now()}`,
      name: file.name,
      size: formatSize(file.size),
      type: file.type,
      docType: slotId,
      status: 'uploading',
    };
    setUploads((prev) => ({ ...prev, [slotId]: newFile }));

    // TODO: Upload to S3 via /api/documents/upload
    setTimeout(() => {
      setUploads((prev) => ({
        ...prev,
        [slotId]: { ...prev[slotId], status: 'uploaded' },
      }));
      toast.success(`${file.name} uploaded successfully`);
    }, 1500);
  };

  const handleFileSelect = (slotId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large — maximum size is 25MB');
      return;
    }
    simulateUpload(slotId, file);
  };

  const removeUpload = (slotId: string) => {
    setUploads((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  };

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 5 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Upload your documents</h2>
        <p className="text-sm text-gray-500">
          Drag and drop or click to upload. AI will validate each document automatically.
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3 p-3.5 bg-blue-50 border border-blue-100 rounded-xl">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-blue-800">Upload progress</span>
            <span className="text-xs font-bold text-blue-700 tabular-nums">{uploadedRequired} / {requiredCount} required</span>
          </div>
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full transition-all duration-500"
              style={{ width: `${(uploadedRequired / requiredCount) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Document slots */}
      <div className="space-y-2.5">
        {docSlots.map((slot) => {
          const uploaded = uploads[slot.id];
          const isDragging = dragOver === slot.id;

          return (
            <div key={slot.id}>
              {uploaded ? (
                <div className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-200 ${
                  uploaded.status === 'uploading' ?'border-blue-200 bg-blue-50' :'border-green-200 bg-green-50'
                }`}>
                  {uploaded.status === 'uploading' ? (
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                      <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={16} className="text-green-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{uploaded.name}</p>
                    <p className="text-xs text-gray-400">{slot.label} · {uploaded.size}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      className="p-1.5 rounded-lg hover:bg-white text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Preview document"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => removeUpload(slot.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label="Remove document"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(slot.id); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(null);
                    handleFileSelect(slot.id, e.dataTransfer.files);
                  }}
                  onClick={() => fileRefs.current[slot.id]?.click()}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-150 ${
                    isDragging
                      ? 'border-blue-400 bg-blue-50' :'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <Upload size={14} className="text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700">{slot.label}</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG — max 25MB</p>
                  </div>
                  {slot.required ? (
                    <span className="badge-red text-xs shrink-0">Required</span>
                  ) : (
                    <span className="badge-gray text-xs shrink-0">Optional</span>
                  )}
                  <input
                    type="file"
                    accept={slot.accept}
                    className="hidden"
                    ref={(el) => { fileRefs.current[slot.id] = el; }}
                    onChange={(e) => handleFileSelect(slot.id, e.target.files)}
                    aria-label={`Upload ${slot.label}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Encryption note */}
      <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
        <AlertCircle size={13} className="text-blue-400 shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500 leading-relaxed">
          Sensitive fields (passport number, financial figures) are automatically blurred in previews. All files are encrypted with AES-256 before storage.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className="btn-primary flex-1 justify-center py-2.5 text-sm"
        >
          Validate my documents
          <ArrowRight size={15} />
        </button>
      </div>
      {!canContinue && (
        <p className="text-xs text-center text-gray-400">Upload at least 4 required documents to continue</p>
      )}
    </div>
  );
}